import React, {
    useState,
    useCallback,
    SyntheticEvent,
    useRef,
    useEffect,
    useMemo,
} from "react";

import "./styles.css";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";

import {
    Box,
    FormControl,
    FormGroup,
    InputLabel,
    Paper,
    Stack,
    styled,
    Switch,
    SwitchProps,
    TextField,
    Typography,
} from "@mui/material";

import { Toast } from "primereact/toast";
import Footer from "../../../components/Footer";
import RecoveryModal from "./components/RecoveryModal";
import loginByEmailAndPassword from "../../../resources/Api/authenticator/loginByEmailAndPassword";
import sendPasswordResetByUserEmail from "../../../resources/Api/authenticator/sendPasswordResetByUserEmail";
import { MeetingRoomOutlined, PinOutlined, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useCookies } from "react-cookie";
import { cpfFormatter, setToastRefToOpen } from "../../../functions/functions";

/**
 * Login - Component that renders a page to login into system
 * @component
 */
const Login = () => {
    // atoms

    // functionalities states
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [loading, setLoading] = useState<boolean>(false);

    //states
    const [userEmail, setUserEmail] = useState<string>("");
    const [userSendEmail, setUserSendEmail] = useState<string>("");
    const [userPassword, setUserPassword] = useState("");

    const [driverCpf, setDriverCpf] = useState<string | null>("");
    const [isDriverLogin, setIsDriverLogin] = useState<boolean>(false);

    // refs
    const toastRef = useRef<Toast>(null);
    const toastModalRef = useRef<Toast>(null);
    const recaptchaRenderRequestedRef = useRef<boolean>(false);

    //cookies
    const [_, setCookie, removeCookies] = useCookies(["selectedParameters", "token"]);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: SyntheticEvent) => {
        event.preventDefault();
    };

    // const navigation = useNavigate();

    useEffect(() => {
        const ref = modalVisible ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        modalVisible,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible
    ]);

    const handleChange = useCallback((name: string, value: any) => {
        if (name === "email") {
            setUserEmail(value);
        }
        if (name === "password") {
            setUserPassword(value);
        }
        if (name === "sendEmail") {
            setUserSendEmail(value);
        }
    }, []);

    /*const getRecaptchaTokenValidation = useCallback(async (tk: string, action: string, siteKey: string) => {
        const response = await axios.post("https://recaptchaenterprise.googleapis.com/v3/projects/praxis-surface-426819-h5/assessments?key=6LetOp0qAAAAAMYufDxn4bwaPKbZMNQkVmWqnPHu", {
            event: {
                token: tk,
                expectedAction: action,
                siteKey: siteKey,
            }
        });
        return response;
    }, []);*/

    const verifyBeforeLogin = useCallback(async () => {
        if (userEmail && userPassword && !isDriverLogin || (driverCpf && userPassword && isDriverLogin)) {
            setLoading(true);

            const emailLogin = userEmail ? userEmail : null;
            const cpfLogin = driverCpf ? driverCpf : null;

            setTimeout(async () => {
                const recaptchaToken = localStorage.getItem("_grecaptcha");
                if (process.env.NODE_ENV === "development" || recaptchaToken) {
                    try {
                        // const tokenValidation = await getRecaptchaTokenValidation(recaptchaToken, "login", "6LetOp0qAAAAAMYufDxn4bwaPKbZMNQkVmWqnPHu");
                        const { data } = await loginByEmailAndPassword(emailLogin, cpfLogin, userPassword);
                        setCookie("token", data);
                        const OldImpersonating = localStorage.getItem("impersonating"); 
                        if (OldImpersonating) {
                            localStorage.removeItem("impersonating");
                        }
                    } catch (error: any) {
                        const { data } = error.response

                        setLoading(false);
                        setFieldsIsValid(false);
                        setTextValidation(data.reason);
                    } finally {
                        setTimeout(() => {
                            setLoading(false);
                            setFieldsIsValid(true);
                            setUserEmail("");
                            setUserSendEmail("");
                            setUserPassword("");
                        }, 3000);
                    }
                } else {
                    setLoading(false);
                    setFieldsIsValid(false);
                    setTextValidation("Marque que você não é um robô.");

                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                }
            }, 2000);
        } else {
            setFieldsIsValid(false);
            setTextValidation("Os campos são obrigatórios, tente novamente!");
            setTimeout(() => {
                setLoading(false);
                setFieldsIsValid(true);
            }, 3000);
        }
    }, [userEmail, userPassword, driverCpf, isDriverLogin, setCookie]);

    const verifyBeforeResetPassword = useCallback(async () => {
        if (!userSendEmail) {
            setFieldsIsValid(false);
            setTextValidation("Preencha um e-mail válido.");
            setTimeout(() => {
                setFieldsIsValid(true);
            }, 3000);
        } else {
            try {
                await sendPasswordResetByUserEmail({ email: userSendEmail });
                setTextValidation("O e-mail foi enviado, favor verifique sua caixa de entrada para redefinir sua senha.");
                setStatusMessage("success");
                setFieldsIsValid(false);
            } catch (error: any) {
                const { data } = error.response

                setLoading(false);
                setFieldsIsValid(false);
                setStatusMessage("warn");
                setTextValidation(data.reason);
            } finally {
                setTimeout(() => {
                    setLoading(false);
                    setFieldsIsValid(true);
                    setUserEmail("");
                    setUserSendEmail("");
                    setUserPassword("");
                }, 3000);
            }
        }
    }, [userSendEmail]);

    /*const getRecaptchaToken = useCallback(() => {
        const windowT = window as any;
        windowT?.grecaptcha?.enterprise.ready(async () => {
            await windowT?.grecaptcha?.enterprise.execute('AIzaSyBYJYkR2SAIfO-UdZd-n3E6SoU2mZxQ0Do', { action: 'login' });
        });
    }, []);*/

    const onRefreshRecaptchaToken = useCallback(() => {
        const recaptchaToken = localStorage.getItem("_grecaptcha");
        if (recaptchaToken) {
            localStorage.removeItem("_grecaptcha");
        }
    }, []);

    useEffect(() => {
        onRefreshRecaptchaToken();
    }, [onRefreshRecaptchaToken]);

    const onRenderRecaptcha = useCallback(async () => {
        const windowT = window as any;
        const element = document.querySelector(".recaptcha-container");
        const recaptcha = windowT?.grecaptcha?.enterprise;

        if (element && recaptcha && !element.hasChildNodes() && !recaptchaRenderRequestedRef.current) {
            recaptchaRenderRequestedRef.current = true;
            recaptcha.ready(async () => {
                if (!element.isConnected || element.hasChildNodes()) return;

                try {
                    await recaptcha.render(element, {
                        sitekey: '6LceAiwqAAAAAKCvk5UM8eOKpZopLT0A3KT4vQdd',
                        theme: "light"
                    });
                } catch (error) {
                    recaptchaRenderRequestedRef.current = false;
                    console.error(error);
                }
            });
        }
    }, []);

    useEffect(() => {
        onRenderRecaptcha();
    }, [onRenderRecaptcha]);

    const IOSSwitch = useMemo(() => styled((props: SwitchProps) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#65C466',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    })), []);

    return (
        <>
            <Box className="container-fluid login-container row justify-content-sm-center !tw-m-0 !tw-flex !tw-h-[100dvh] !tw-w-full !tw-flex-nowrap !tw-overflow-hidden !tw-p-0"
                style={{
                    backgroundImage: `url(${require("../../../assets/files/image.png")})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "repeat",
                }}
            >
                <RecoveryModal
                    handleChange={handleChange}
                    onSend={verifyBeforeResetPassword}
                    onClose={() => {
                        if (!isToastVisible) {
                            setModalVisible(false);
                            setUserEmail("");
                            setUserPassword("");
                            setUserSendEmail("");
                        }
                    }}
                    loading={loading}
                    isVisible={modalVisible}
                    toast={
                        <div
                            style={{
                                top: 0,
                                height: 50,
                                width: 50,
                                position: "absolute",
                                zIndex: 9999999,
                            }}
                        >
                            <Toast
                                ref={toastModalRef}
                                position="top-right"
                                onShow={() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(true);

                                    if (validationText) {
                                        setTimeout(() => {
                                            setTextValidation("");
                                            setIsToastVisible(false);
                                        }, 3000);
                                    }
                                }}
                            />
                        </div>
                    }
                    fieldValue={userSendEmail}
                />
                <Box
                    className="d-none d-lg-flex col-lg-12 tw-relative lg:!tw-flex lg:!tw-w-3/5 lg:tw-items-center lg:tw-justify-center"
                    sx={{
                        display: "flex",
                        height: "100vh",
                        width: "60%",
                        justifyContent: "center",
                        alignItems: "center",
                        left: 0,
                        background: "linear-gradient(135deg, rgba(0, 63, 74, 0.82), rgba(9, 65, 74, 0.68))",
                    }}
                >
                    <img
                        alt="GTR"
                        className="d-none d-lg-block tw-drop-shadow-2xl"
                        src={require("../../../assets/files/gtf-white.png")}
                        style={{
                            position: "absolute",
                            height: 200,
                            width: 380,
                            justifySelf: "center",
                        }}
                    />
                </Box>
                <Box
                    // className="row col-lg-12 login-background"
                    className="row col-lg-12 login-background !tw-m-0 !tw-flex !tw-h-[100dvh] !tw-w-full !tw-flex-col !tw-flex-nowrap !tw-items-center !tw-justify-center !tw-overflow-y-auto !tw-bg-slate-50/95 !tw-p-4 dark:!tw-bg-slate-950/95 lg:!tw-w-2/5"
                    style={{
                        display: "flex",
                        height: "100vh",
                        justifyContent: "center",
                        justifyItems: "center",
                        alignItems: "center",
                        margin: 0,
                    }}
                >
                    {!loading && <Box
                        className="d-none d-lg-grid tw-mb-4 tw-place-items-center tw-gap-1"
                        sx={{
                            display: "grid",
                            justifyContent: "center",
                            alignItems: "center",
                            alignContent: "center"
                        }}
                    >
                        <Box className="tw-flex tw-items-center tw-justify-center">
                            <img
                                alt="Rastreamento"
                                src={require("../../../assets/files/tracker.png")}
                                className="tw-drop-shadow-md"
                                style={{ height: 104, width: 104 }}
                            />
                        </Box>
                        <Box className="tw-flex tw-items-center tw-justify-center">
                            <Typography
                                variant="h4"
                                style={{ fontFamily: "Prometo", color: "#003f4a", fontWeight: 700 }}
                                className="title-on-header"
                            >
                                Rastreamento
                            </Typography>
                        </Box>
                    </Box>}
                    {loading && (
                        <div
                            style={{
                                width: "100%",
                                padding: 20,
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <div className="tracker-loading"><div className="ld-tracker">
                                <div></div><div></div><div></div>
                            </div></div>
                        </div>
                    )}
                    {!loading && (
                        <Paper
                            className="card card-container login !tw-h-auto !tw-w-full !tw-max-w-md !tw-rounded-2xl !tw-border-0 !tw-bg-white !tw-p-6 !tw-shadow-xl dark:!tw-bg-slate-900 sm:!tw-p-8"
                            elevation={0}
                            sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                                "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700", borderWidth: 2 },
                            }}
                        >
                            <Toast
                                ref={toastRef}
                                position="top-right"
                                onShow={() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(true);
                                    if (validationText) {
                                        setTimeout(() => {
                                            setTextValidation("");
                                            setIsToastVisible(false);
                                        }, 3000);
                                    }
                                }}
                            />

                            <Box
                                className="col-md-12 tw-w-full"
                                sx={{ margin: 0, marginBottom: 3 }}
                            >
                                <FormGroup>
                                    <Stack className="tw-rounded-xl tw-bg-slate-50 tw-p-3 dark:tw-bg-slate-800" direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                                        <IOSSwitch
                                            title="logar como condutor"
                                            checked={isDriverLogin}
                                            disabled={(false) as boolean}
                                            onChange={(event) => {
                                                const checked = event.target.checked;

                                                if (!checked) {
                                                    setDriverCpf("");
                                                }
                                                setUserEmail("");
                                                setIsDriverLogin(checked);
                                            }}
                                            inputProps={{ 'aria-label': 'ant design' }}
                                        />
                                        <Typography sx={{ color: "#52666a", fontFamily: "Prometo", fontSize: 14, fontWeight: 600 }}>Login condutor</Typography>
                                    </Stack>
                                </FormGroup>
                            </Box>

                            <Box className="col-md-12 tw-mb-5 tw-w-full" style={{ marginBottom: 20 }}>

                                {isDriverLogin && <FormControl
                                    className="form-group"
                                    style={{ display: "flex", width: "100%" }}
                                >
                                    <InputLabel
                                        id="demo-multiple-cpf-label"
                                        shrink
                                        required={!isDriverLogin}
                                        disabled={
                                            false
                                        }
                                    >
                                        CPF/Matricula
                                    </InputLabel>
                                    <TextField
                                        name="cpf"
                                        type="cpf"
                                        required={!isDriverLogin}
                                        autoComplete="off"
                                        inputProps={{ maxLength: 11 }}
                                        InputProps={{ label: "CPF/Matrícula", notched: true }}
                                        placeholder="Digite o CPF/Matrícula"
                                        className="form-control driver-cpf"
                                        value={cpfFormatter(driverCpf ?? "")}
                                        onChange={(
                                            event: React.ChangeEvent<HTMLInputElement>
                                        ) => {
                                            setDriverCpf(event.target.value);
                                        }}
                                        disabled={
                                            false
                                        }
                                    />
                                    <IconButton
                                        className="visibility-password"
                                        disabled
                                        onMouseDown={(e: SyntheticEvent) => { }}
                                        edge="end"
                                        size="large"
                                    >
                                        <PinOutlined />
                                    </IconButton>
                                </FormControl>}

                                {!isDriverLogin && <FormControl
                                    className="form-group"
                                    style={{ display: "flex", width: "100%" }}
                                >
                                    <InputLabel
                                        id="demo-multiple-mail-label"
                                        shrink
                                        style={{ fontFamily: "Prometo" }}
                                    >
                                        E-mail/Login
                                    </InputLabel>
                                    <TextField
                                        name="email"
                                        type="email"
                                        autoComplete="off"
                                        autoFocus
                                        InputProps={{
                                            label: "E-mail/login",
                                            notched: true,
                                            style: { fontFamily: "Prometo" },
                                        }}
                                        placeholder="Digite o e-mail ou login"
                                        className="form-control form-control-login"
                                        value={userEmail}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            handleChange(event.target.name, event.target.value)
                                        }
                                    />
                                </FormControl>}
                            </Box>

                            <Box className="col-md-12 tw-mb-5 tw-w-full" style={{ marginBottom: 20 }}>
                                <FormControl
                                    className="form-group"
                                    style={{ display: "flex", width: "100%" }}
                                >
                                    <InputLabel
                                        id="demo-multiple-password-label"
                                        shrink
                                        style={{ fontFamily: "Prometo" }}
                                    >
                                        Senha
                                    </InputLabel>
                                    <TextField
                                        name="password"
                                        autoComplete="off"
                                        onKeyDown={(e) => (
                                            e.key === "Enter" ? verifyBeforeLogin() : null
                                        )}
                                        InputProps={{
                                            label: "Senha",
                                            notched: true,
                                            style: { fontFamily: "Prometo" },
                                        }}
                                        value={userPassword}
                                        onChange={(e) =>
                                            handleChange(e.target.name, e.target.value)
                                        }
                                        type={showPassword ? "text" : "password"}
                                        className="form-control form-control-login"
                                        placeholder="Digite sua senha"
                                    />
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        className="visibility-password"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={(e: SyntheticEvent) => {
                                            handleMouseDownPassword(e);
                                        }}
                                        edge="end"
                                        size="large"
                                    >
                                        {showPassword ? (
                                            <VisibilityOffOutlined />
                                        ) : (
                                            <VisibilityOutlined />
                                        )}
                                    </IconButton>
                                </FormControl>
                            </Box>

                            <Box className="col-md-12 d-flex justify-content-center mb-2 tw-min-h-[78px] tw-w-full tw-items-center tw-overflow-hidden">
                                <Box className="recaptcha-container"></Box>
                            </Box>
                            <Box className="col-md-12 d-flex justify-content-center tw-w-full">
                                <Button
                                    type="button"
                                    style={{ fontFamily: "Prometo", color: "#ffff" }}
                                    onClick={() => {
                                        verifyBeforeLogin();
                                    }}
                                    className="btn btn-primary btn-block login-button tw-shadow-sm tw-transition-all tw-duration-200"
                                >
                                    <MeetingRoomOutlined style={{ marginRight: 5 }} />
                                    <Typography
                                        className="forgot-password"
                                        style={{ fontFamily: "Prometo", fontSize: "16px", color: "#ffff" }}
                                    >Entrar</Typography>
                                </Button>
                            </Box>

                            <Box className="tw-mt-5">
                                <Box
                                    className="col-md-12 d-flex justify-content-center tw-w-full"
                                >
                                    <Typography
                                        className="forgot-password text-right"
                                        style={{ fontFamily: "Prometo", fontSize: "14px", color: "#52666a" }}
                                    >
                                        Esqueceu a senha?
                                        <Button
                                            className="forgot-password-button"
                                            size="small"
                                            onClick={() => {
                                                setUserEmail("");
                                                setModalVisible(true);
                                            }}
                                        >
                                            clique aqui
                                        </Button>{" "}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}
                    {!loading && <Footer />}
                </Box>
            </Box>
        </>
    );
};

export default Login;
